import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { LoadingOverlay as LoadingOverlayImport } from '../../../../core/screens/LoadingOverlay/LoadingOverlay';
import { Credentials } from '../../../../models/credentials';
import { AppState, ConnectedPropsWithoutDispatch } from '../../../../stores/redux-store';
import { usePaymentDetails as usePaymentDetailsImport } from '../../hooks/usePaymentDetails/usePaymentDetails';
import { useProviderPaymentConditions as useProviderPaymentConditionsImport } from '../../hooks/useProviderPaymentConditions/useProviderPaymentConditions';
import {
  isSwedbankOrSavingsbank,
  SwedbankSavingsbankErrorOverlay as SwedbankSavingsbankErrorOverlayImport,
  SwedbankSavingsbankErrorOverlayOwnProps,
} from '../PaymentDirector/molecules/SwedbankSavingsbankErrorOverlay/SwedbankSavingsbankErrorOverlay';
import {
  SubmitCredentialsControllerOwnProps,
  SubmitCredentialsControllerPay as SubmitCredentialsControllerPayImport,
} from '../screens/SubmitCredentialsPay/SubmitCredentialsControllerPay';
import { useSelectedProvider as useSelectedProviderImport } from '../../../../components/App/ProviderContainer/useSelectedProvider';
import { useAnalytics } from '../../../../core/Analytics/AnalyticsProvider';
import { Transfer } from '../../../DirectPayments/models/transfer';
import {
  ParentApplicationEvent,
  parentMessenger,
} from '../../../../core/Analytics/ParentApplicationEventMessenger';
import { ApplicationEvent } from '../../../../models/analytics';
import { SessionTransferWrapper as SessionTransferWrapperImport } from './SessionTransferWrapper/SessionTransferWrapper';

type InstantPISDirectorProps = PropsFromRedux & {
  useSelectedProvider?: typeof useSelectedProviderImport;
  usePaymentDetails?: typeof usePaymentDetailsImport;
  useProviderPaymentConditions?: typeof useProviderPaymentConditionsImport;
  LoadingOverlay?: React.ComponentType;
  SubmitCredentialsControllerPay?: React.ComponentType<SubmitCredentialsControllerOwnProps>;
  SessionTransferWrapper?: React.ComponentType;
  SwedbankSavingsbankErrorOverlay?: React.ComponentType<SwedbankSavingsbankErrorOverlayOwnProps>;
};

export const InstantPISDirectorPresenter: React.FC<InstantPISDirectorProps> = ({
  credentials,
  transfer,
  LoadingOverlay = LoadingOverlayImport,
  SwedbankSavingsbankErrorOverlay = SwedbankSavingsbankErrorOverlayImport,
  SessionTransferWrapper = SessionTransferWrapperImport,
  SubmitCredentialsControllerPay = SubmitCredentialsControllerPayImport,
  useSelectedProvider = useSelectedProviderImport,
  usePaymentDetails = usePaymentDetailsImport,
  useProviderPaymentConditions = useProviderPaymentConditionsImport,
}) => {
  const { selectedProvider } = useSelectedProvider();
  const paymentDetails = usePaymentDetails();
  const providerPaymentConditions = useProviderPaymentConditions();
  const { tinklinkAnalytics } = useAnalytics();
  const isTransferSent = Transfer.isSent(transfer);

  useEffect(() => {
    if (isTransferSent) {
      parentMessenger.fireApplicationEvent(ParentApplicationEvent.PAYMENT_SUCCESSFUL);
      tinklinkAnalytics.sendApplicationEvent(ApplicationEvent.PAYMENT_SUCCESSFUL);
    }
  }, [isTransferSent, tinklinkAnalytics]);

  if (!paymentDetails || !providerPaymentConditions) {
    return <LoadingOverlay />;
  }

  const isSwedbankOrSavingsbankError =
    credentials && Credentials.isSwedbankSavingsbankError(credentials);

  if (isSwedbankOrSavingsbank(selectedProvider.name) && isSwedbankOrSavingsbankError) {
    return <SwedbankSavingsbankErrorOverlay provider={selectedProvider} />;
  }

  if (Credentials.isUpdated(credentials) || Credentials.isCreated(credentials)) {
    return <SessionTransferWrapper />;
  }

  return (
    <SubmitCredentialsControllerPay
      provider={selectedProvider}
      isPaymentSca
      onExecute={undefined}
    />
  );
};

const mapStateToProps = (state: AppState) => {
  return {
    transfer: state.transfer.data,
    credentials: state.credentials.data,
  };
};

const connector = connect(mapStateToProps);

type PropsFromRedux = ConnectedPropsWithoutDispatch<typeof connector>;

export const InstantPISDirector = connector(InstantPISDirectorPresenter);
